/**
 * `speak(text)`: one sentence in the agent's voice, through `GET /v1/agent/speech`.
 *
 * The source is the phone's `setupVoiceSource` (`lib/agent-api.ts`), and an
 * audio element streams it: the server answers chunked mpeg, so the first word
 * plays while the rest is still being made. The request is same-origin, so
 * the session cookie rides without being named.
 *
 * Speaking is a small queue. Without `queue` a new sentence cuts off whatever
 * is playing, which is what a reply does; with it the sentence waits its turn,
 * which is what a build's progress lines need.
 *
 * The route speaks for setup only (`VOICE_SETUP_ONLY` inside a business): it is
 * called without a tenant, which is how `setupVoiceSource` builds it.
 */
import type { AgentLanguageTag } from '@alluzo/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { setupVoiceSource } from '@/lib/agent-api';
import { turnTimeline } from '@/lib/turn-timeline';

export type SpeakOptions = {
  /** Play after what is playing instead of cutting it. */
  readonly queue?: boolean;
};

export type Speaker = {
  /** A sentence is playing or being fetched. */
  readonly speaking: boolean;
  readonly speak: (text: string, options?: SpeakOptions) => void;
  readonly stop: () => void;
  /** Whether a sentence is playing or being fetched, read at this instant rather than from state. */
  readonly isTalking: () => boolean;
};

export type SpeakerInput = {
  /** The voice's language, read when a sentence is fetched. Absent is English. */
  readonly language?: AgentLanguageTag;
  /** A sentence could not be fetched or played. The queue goes on without it. */
  readonly onFailed?: () => void;
  /** Called just before a sentence starts, so a screen can close its microphone first. */
  readonly onBeforeSpeak?: () => void;
};

export const useSpeaker = (input: SpeakerInput = {}): Speaker => {
  const called = useRef(input);
  useEffect(() => {
    called.current = input;
  });

  const [speaking, setSpeaking] = useState(false);
  const clip = useRef<HTMLAudioElement | null>(null);
  const queue = useRef<string[]>([]);
  const talking = useRef(false);
  /** Which clip is current: a stop or a newer sentence wins over a late failure. */
  const clipId = useRef(0);

  const release = (held: HTMLAudioElement | null): void => {
    if (held === null) return;
    held.onplaying = null;
    held.onended = null;
    held.onerror = null;
    held.pause();
    held.removeAttribute('src');
    held.load();
  };

  const stop = useCallback(() => {
    clipId.current += 1;
    queue.current = [];
    talking.current = false;
    release(clip.current);
    clip.current = null;
    setSpeaking(false);
  }, []);

  const playRef = useRef<(text: string) => void>(() => undefined);

  const next = useCallback(() => {
    const text = queue.current.shift();
    if (text === undefined) {
      talking.current = false;
      release(clip.current);
      clip.current = null;
      setSpeaking(false);
      return;
    }
    playRef.current(text);
  }, []);

  const play = useCallback(
    (text: string) => {
      const mine = ++clipId.current;
      called.current.onBeforeSpeak?.();
      talking.current = true;
      setSpeaking(true);
      release(clip.current);
      const made = new Audio(setupVoiceSource('', text, called.current.language).uri);
      clip.current = made;
      let started = false;
      /** This clip has ended, one way or the other: an error and a refused play() are one failure, not two. */
      let over = false;
      const failed = (): void => {
        if (over || clipId.current !== mine) return;
        over = true;
        called.current.onFailed?.();
        next();
      };
      made.onplaying = () => {
        // The first sound of a reply: the end of the turn the person is waiting on.
        if (!started) turnTimeline.mark('spoken');
        started = true;
      };
      made.onended = () => {
        if (over || clipId.current !== mine) return;
        over = true;
        next();
      };
      made.onerror = failed;
      made.play().catch(failed);
    },
    [next],
  );
  useEffect(() => {
    playRef.current = play;
  }, [play]);

  const speak = useCallback(
    (text: string, options?: SpeakOptions) => {
      if (text.trim() === '') return;
      if (options?.queue === true && talking.current) {
        queue.current.push(text);
        return;
      }
      if (options?.queue !== true) queue.current = [];
      play(text);
    },
    [play],
  );

  const isTalking = useCallback(() => talking.current, []);

  useEffect(() => () => stop(), [stop]);

  return { speaking, speak, stop, isTalking };
};
